import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VaultKeTheme } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'request' | 'sent'>('request');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '500px',
      width: '100%',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    logo: {
      width: '80px',
      height: '80px',
      backgroundColor: VaultKeTheme.colors.primary,
      borderRadius: '50%',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
    },
    logoText: {
      fontSize: VaultKeTheme.typography.h1.fontSize,
      fontWeight: VaultKeTheme.typography.h1.fontWeight,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    card: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.xl,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.lg,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
    },
    formGroup: {
      marginBottom: VaultKeTheme.spacing.xl,
    },
    label: {
      display: 'block',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    input: {
      width: '100%',
      padding: VaultKeTheme.spacing.md,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.text,
      fontSize: VaultKeTheme.typography.body1.fontSize,
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    inputError: {
      borderColor: VaultKeTheme.colors.error,
    },
    errorText: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      color: VaultKeTheme.colors.error,
      marginTop: VaultKeTheme.spacing.xs,
    },
    button: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    successCard: {
      backgroundColor: VaultKeTheme.colors.success + '15',
      border: `1px solid ${VaultKeTheme.colors.success}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      marginBottom: VaultKeTheme.spacing.xl,
      textAlign: 'center',
    },
    successIcon: {
      fontSize: '48px',
      marginBottom: VaultKeTheme.spacing.md,
    },
    successTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.success,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    successText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
    },
    instructionsCard: {
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      marginBottom: VaultKeTheme.spacing.xl,
    },
    instructionsTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.md,
    },
    instructionsList: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.6',
    },
    secondaryButton: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: 'transparent',
      color: VaultKeTheme.colors.primary,
      border: `1px solid ${VaultKeTheme.colors.primary}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    backButton: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: 'transparent',
      color: VaultKeTheme.colors.textSecondary,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: VaultKeTheme.spacing.sm,
    },
    backIcon: {
      fontSize: '18px',
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!identifier.trim()) {
      setErrors({ identifier: 'Please enter your email or phone number' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStep('sent');
      } else {
        setErrors({ identifier: data.error || 'Failed to send reset instructions' });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrors({ identifier: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    setStep('request');
    setIdentifier('');
    setErrors({});
  };

  if (step === 'sent') {
    return (
      <div style={{ ...styles.page, paddingBottom: '80px' }}>
        <div style={styles.container}>
          <div style={styles.logoContainer}>
            <div style={styles.logo}>VK</div>
            <h1 style={styles.logoText}>VaultKe</h1>
          </div>

          <div style={styles.card}>
            <div style={styles.successCard}>
              <div style={styles.successIcon}>📧</div>
              <h3 style={styles.successTitle}>Check Your Inbox</h3>
              <p style={styles.successText}>
                We've sent password reset instructions to your email or phone number.
              </p>
            </div>

            <div style={styles.instructionsCard}>
              <h4 style={styles.instructionsTitle}>What to do next:</h4>
              <div style={styles.instructionsList}>
                • Check your email inbox and spam folder<br/>
                • Check your SMS messages<br/>
                • Follow the link or code provided<br/>
                • Create a new password
              </div>
            </div>

            <button
              onClick={() => navigate('/reset-password')}
              style={styles.button}
            >
              Enter Reset Code
            </button>

            <button onClick={handleResend} style={styles.secondaryButton}>
              Resend Instructions
            </button>

            <button
              onClick={() => navigate('/login')}
              style={styles.backButton}
            >
              <span style={styles.backIcon}>←</span>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>VK</div>
          <h1 style={styles.logoText}>VaultKe</h1>
        </div>

        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Forgot Password?</h2>
            <p style={styles.subtitle}>Enter your email to reset your password</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email or Phone</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your email or phone"
                style={{
                  ...styles.input,
                  ...(errors.identifier ? styles.inputError : {}),
                }}
              />
              {errors.identifier && <div style={styles.errorText}>{errors.identifier}</div>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...styles.button,
                ...(isLoading ? styles.buttonDisabled : {}),
              }}
            >
              {isLoading ? 'Sending...' : 'Send Reset Instructions'}
            </button>
          </form>

          <button
            onClick={() => navigate('/login')}
            style={styles.backButton}
          >
            <span style={styles.backIcon}>←</span>
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;